import { type KeyboardEvent, useMemo } from 'react';
import { isFunction } from 'lodash/fp';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Defaults } from 'app/lib/constants';
import type { TableColumn } from './types';
import * as styles from './Table.css';

const SORT_ICONS = ['chevron-down-big', 'blank', 'chevron-up-big'] as const;

export interface HeaderCellProps<TRow> {
    column: TableColumn<TRow>;
    sort: -1 | 0 | 1;
    onClick?: (field: string) => void;
}

export function HeaderCell<TRow>({ column, sort, onClick = Defaults.NOOP }: HeaderCellProps<TRow>) {
    const icon = SORT_ICONS[sort + 1]!;
    const { headerRenderer, field, sortable, label } = column;

    function handleClick() {
        onClick(field);
    }

    function handleKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                handleClick();
                break;
            default:
        }
    }

    const style = useMemo(
        () => ({
            minWidth: column.minWidth,
            width: column.width,
            ...column.style
        }),
        [column]
    );

    return (
        <th
            className={styles.cell}
            tabIndex={sortable ? 0 : undefined}
            onClick={sortable ? handleClick : undefined}
            onKeyDown={sortable ? handleKeyDown : undefined}
            style={style}>
            <div>
                {isFunction(headerRenderer) ? headerRenderer(column) : label}
                {sortable && <Icon className={styles.sort} variant={icon} size={IconSize.SMALL} />}
            </div>
        </th>
    );
}
